import { WebshopNotification } from './WebshopNotification';
import { NotificationPriority } from './NotificationPriority.enum';
import { WebshopResponsiveConfiguration } from '../configuration/webshop-responsive.configuration';
import { CssDisplayMode } from '../dom/CSSDefinitions.enum';
import { DOMUtil } from '../dom';
import { StorageService } from '../storage';
import { LocalStoreKey } from '../storage/local-store-key.enum';
import { LocalStore } from '../storage/storage.interface';
import {
    AnalyticsEventName,
    CustomEvent,
    GAEventAction,
    GAEventCategory,
    GTMUtils
} from "../google-tag-manager";

/** Typings for NotificationService on window object */
declare global {
    interface Window {
        NotificationManager: NotificationManager;
    }
}

/**
 * Notification service
 */
export class NotificationManager {

    public static getInstance(configuration: WebshopResponsiveConfiguration): NotificationManager {
        const windowInstance = this.getDOMInstance();

        if (windowInstance &&
            windowInstance.getConfig().poolId === configuration.poolId &&
            windowInstance.getConfig().notificationLimit === configuration.notificationLimit) {
            return windowInstance;
        }

        if (!this.instance) {
            this.instance = new NotificationManager(configuration);
            window.NotificationManager = this.instance;
        }

        return this.instance;
    }

    private static instance: NotificationManager;

    private static getDOMInstance(): NotificationManager | undefined {
        if (!window) {
            return undefined;
        }

        if (window.NotificationManager === undefined) {
            return undefined;
        }

        return window.NotificationManager;
    }

    private readonly containerElement: Element;
    private readonly containerId: string = 'Notification-Container';
    private readonly hasPriorityNotification: boolean;
    private readonly maxNotifications: number;
    private readonly session: StorageService;

    private activeNotifications: Map<string, WebshopNotification> = new Map();
    private queuedNotifications: Set<WebshopNotification> = new Set(null);

    constructor(private configuration: WebshopResponsiveConfiguration) {
        this.session = StorageService.getInstance({ poolId: configuration.poolId! });

        const selector = `[data-js=${this.containerId}]`;
        const containerCount = document.querySelectorAll(selector).length;

        if (containerCount === 0) {
            throw new Error('NotificationManager MUST have notification placeholder element defined in template!');
        }

        if (containerCount > 1) {
            throw new Error('NotificationManager MUST have only ONE notification placeholder element defined in template!');
        }

        this.containerElement = document.querySelector(selector)!;
        this.maxNotifications = configuration.notificationLimit !== null ? configuration.notificationLimit : 2;
        this.hasPriorityNotification = document.querySelectorAll(`[data-js=${WebshopNotification.priorityHighId}]`).length > 0;

        const config = { attributes: false, childList: true, subtree: false };
        let container = this.containerElement;
        let footerElement = document.querySelector('[data-js=footer]');

        let observer = new MutationObserver(function (mutationList: MutationRecord[]) {
            for (let mutation of mutationList) {
                (footerElement as HTMLElement).style.paddingBottom = (mutation.target as HTMLElement).offsetHeight + 'px';
            }
        });

        observer.observe(this.containerElement, config);

        window.addEventListener('orientationchange', function () {
            if (footerElement !== null) {
                (footerElement as HTMLElement).style.paddingBottom = (container as HTMLElement).offsetHeight + 'px';
            }
        })
    }

    public getConfig(): WebshopResponsiveConfiguration {
        return this.configuration;
    }

    /**
     * Register notification to queue
     * @param notification
     */
    public register(notification: WebshopNotification): boolean {
        // console.log('Register notification call:', notification);
        if (!this.canRegister(notification)) {
            throw new Error('NotificationService MUST only register single HIGH priority notification');
        }

        // Has notification been resolved
        // console.log(this.hasAlreadyResolved(notification));
        if (this.hasAlreadyResolved(notification)) {
            return false;
        }

        // Is notification already active
        // console.log('Is notification active?', this.activeNotifications.has(notification.notificationId));
        if (this.activeNotifications.has(notification.notificationId)) {
            return false;
        }

        // Has notification not been queued
        // console.log('Is notification queued?', this.queuedNotifications.has(notification));
        if (!this.queuedNotifications.has(notification)) {
            this.queuedNotifications.add(notification);
        }

        return this.publish(notification);
    }

    /**
     * Resolve notification (remove from display) and publish next queued notification
     * @param notification
     */
    public resolve(notification: WebshopNotification): boolean {
        const notificationNode = this.containerElement
            .querySelector(`[data-js-id=${notification.notificationId}]`);

        if (notificationNode) {
            // Remove notification from DOM
            this.containerElement.removeChild(notificationNode);

            // Remove notification from active notifications
            this.activeNotifications.delete(notification.notificationId);

            // Set notification resolved state in session store
            if (!notification.getPersistence()) {
                this.session.savePersistence(this.prepareNotificationState(notification));
            }

            // Publish next queued notification
            if (this.queuedNotifications !== undefined && this.queuedNotifications.size > 0) {
                const newToPublish = [...this.queuedNotifications].shift();
                if (newToPublish) {
                    this.publish(newToPublish);
                }
            }

            return true;
        }

        return false;
    }

    /**
     * Publish (display) notification
     * @param notification
     */
    private publish(notification: WebshopNotification): boolean {
        if (!this.canPublish(notification)) {
            return false;
        }

        // Set notification as active
        this.activeNotifications.set(notification.notificationId, notification);

        // Remove notification from queue
        this.queuedNotifications.delete(notification);

        // Display notification
        let DOMinsertion: boolean;

        if (notification.getPriority() === NotificationPriority.High) {
            DOMinsertion = this.containerElement.insertAdjacentElement(
                'beforeend',
                notification.getNotificationElement()
            ) !== null;
        } else {
            DOMinsertion = this.containerElement.insertAdjacentElement(
                'afterbegin',
                notification.getNotificationElement()
            ) !== null;
        }

        if (DOMinsertion && notification.notificationId === 'Notification-permissions-specials') {
            GTMUtils.pushAnalyticsEventToDataLayer({
                event: AnalyticsEventName.PushNotificationShown,
            });
        }

        return DOMinsertion;
    }

    /**
     * Validates if notification can be published
     * @param notification
     */
    private canPublish(notification: WebshopNotification): boolean {

        if (window.location.href.indexOf('checkout') > 0) {
            return false;
        }

        if (notification.getPriority() === NotificationPriority.High) {
            return true;
        }

        let availableSpace = this.maxNotifications - this.activeNotifications.size;

        if (this.hasPriorityNotification && this.hasActiveHighPriorityNotifications()) {
            availableSpace = availableSpace - 1;
        }

        return availableSpace > 0;
    }

    /**
     * Validates if notification can be registered
     * @param notification
     */
    private canRegister(notification: WebshopNotification): boolean {
        if (notification.getPriority() === NotificationPriority.Normal) {
            return true;
        }

        return !this.hasQueuedHighPriorityNotifications()
            && !this.hasActiveHighPriorityNotifications();
    }

    /**
     * Already resolved notification check (if entry exists in localStorage)
     * @param notification
     */
    private hasAlreadyResolved(notification: WebshopNotification): boolean {
        const persistence = this.session.loadPersistence();
        const notificationStore = persistence ? persistence[LocalStoreKey.Notifications] : undefined;

        if (!notificationStore || !notificationStore[notification.notificationId]) {
            return false;
        }

        if (DOMUtil.isWindowInDisplayMode(CssDisplayMode.Standalone) && this.hasExpiredTTL(notification)) {
            return false;
        }

        return notificationStore[notification.notificationId].isResolved;
    }

    /**
     * Check if priority notification is active
     */
    private hasActiveHighPriorityNotifications(): boolean {
        let hasActive: boolean = false;

        this.activeNotifications.forEach(notification => {
            if (!hasActive) {
                hasActive = notification.getPriority() === NotificationPriority.High;
            }
        });

        return hasActive;
    }

    /**
     * Check if priority notification is queued
     */
    private hasQueuedHighPriorityNotifications(): boolean {
        let hasQueued: boolean = false;

        this.queuedNotifications.forEach(notification => {
            if (!hasQueued) {
                hasQueued = notification.getPriority() === NotificationPriority.High;
            }
        });

        return hasQueued;
    }

    /**
     * Returns updated LocalStore state for notification
     * @param notification
     */
    private prepareNotificationState(notification: WebshopNotification): LocalStore {
        const date: Date = new Date();
        const session = this.session.loadPersistence();
        const store: LocalStore = (session && session[LocalStoreKey.Notifications])
            ? session as LocalStore
            : { [LocalStoreKey.Notifications]: {} } as LocalStore;

        store[LocalStoreKey.Notifications]![notification.notificationId]! = {
            id: notification.notificationId,
            isResolved: true,
            ttl: (notification.ttl) ? date.getTime() + notification.ttl : null
        };

        return store;
    }

    /**
     * Check if notification TTL for store has expired
     * @param notification
     */
    private hasExpiredTTL(notification: WebshopNotification): boolean {
        const date = new Date();
        const persistence = this.session.loadPersistence();
        const notificationStore = persistence ? persistence[LocalStoreKey.Notifications] : null;

        if (!notificationStore || !notificationStore[notification.notificationId]) {
            return false;
        }

        return !!notificationStore[notification.notificationId]
                && notificationStore[notification.notificationId]!.ttl !== null
                && date.getTime() > notificationStore[notification.notificationId]!.ttl!;
    }
}
